import * as React from 'react'
import { AlertTriangle, Check, FilePlus2, FileUp, RefreshCw } from 'lucide-react'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import type { ImportKind, ImportStep, ImportSummary, MappingOption, UploadState } from './types'
import { RowErrorsTable } from './RowErrorsTable'
import { CsvFilePicker } from './CsvFilePicker'

const STEPS: { key: ImportStep; label: string }[] = [
  { key: 'upload', label: 'Upload file' },
  { key: 'mapping', label: 'Map columns' },
  { key: 'preview', label: 'Preview' },
  { key: 'results', label: 'Results' },
]

export function Stepper({
  current,
  steps = STEPS,
}: {
  current: string
  steps?: { key: string; label: string }[]
}) {
  const currentIndex = steps.findIndex((step) => step.key === current)

  return (
    <ol aria-label="Import steps" className="flex flex-wrap items-center gap-2">
      {steps.map((step, index) => {
        const done = index < currentIndex
        const active = index === currentIndex

        return (
          <li key={step.key} className="flex items-center gap-2">
            <span className="flex items-center gap-2">
              <span
                className={cn(
                  'flex h-5 w-5 items-center justify-center rounded-full border text-caption',
                  done && 'border-primary bg-primary text-primary-foreground',
                  active && 'border-primary text-primary',
                  !done && !active && 'border-border text-muted-foreground',
                )}
              >
                {done ? <Check className="h-3 w-3" strokeWidth={2.5} /> : index + 1}
              </span>
              <span
                className={cn('text-body-sm', active ? 'text-foreground' : 'text-muted-foreground')}
              >
                {step.label}
              </span>
            </span>
            {index < steps.length - 1 && <span className="h-px w-6 bg-border" />}
          </li>
        )
      })}
    </ol>
  )
}

export function UploadStep({
  kind,
  file,
  message,
  onKindChange,
  onFileChange,
  onContinue,
}: {
  kind: ImportKind
  file: File | null
  message: string | null
  onKindChange: (kind: ImportKind) => void
  onFileChange: (file: File | null) => void
  onContinue: () => void
}) {
  return (
    <div className="space-y-4">
      <Card data-testid="contact-import-upload-panel" className="p-4">
        <div className="space-y-4">
          <div className="max-w-xs space-y-1.5">
            <Label htmlFor="contact-import-kind">Import as</Label>
            <Select value={kind} onValueChange={(value) => onKindChange(value as ImportKind)}>
              <SelectTrigger id="contact-import-kind" className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="People">People</SelectItem>
                <SelectItem value="Organizations">Organizations</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <CsvFilePicker
            testId="contact-import-dropzone"
            onFileChange={onFileChange}
            description="Up to 10,000 rows; UTF-8 encoded; first row is headers"
          />
        </div>
      </Card>

      <div className="space-y-3">
        {message && <Alert className="w-full text-left">{message}</Alert>}
        {file && (
          <p className="text-body-sm text-muted-foreground">
            Selected <span className="text-foreground">{file.name}</span>
          </p>
        )}
        <div className="flex justify-end">
          <Button type="button" size="sm" disabled={!file} onClick={onContinue}>
            <FileUp className="mr-2 h-4 w-4" strokeWidth={1.5} />
            Map columns
          </Button>
        </div>
      </div>
    </div>
  )
}

export function MappingStep({
  upload,
  options,
  pending,
  error,
  onBack,
  onMappingChange,
  onPreview,
}: {
  upload: UploadState
  options: MappingOption[]
  pending: boolean
  error: string | null
  onBack: () => void
  onMappingChange: (header: string, field: string) => void
  onPreview: () => void
}) {
  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="flex flex-col gap-4">
          <h2 className="text-body-sm font-medium text-foreground">Map your columns</h2>
          {error && <Alert>{error}</Alert>}
          <div className="overflow-hidden rounded-md border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>CSV column</TableHead>
                  <TableHead>Sample value</TableHead>
                  <TableHead className="w-56">Maps to</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {upload.columns.map((column) => (
                  <TableRow key={column.header}>
                    <TableCell>{column.header}</TableCell>
                    <TableCell className="text-muted-foreground">{column.sample || '-'}</TableCell>
                    <TableCell>
                      <Select
                        value={upload.mappings[column.header] ?? 'ignore'}
                        onValueChange={(field) => onMappingChange(column.header, field)}
                      >
                        <SelectTrigger aria-label={`Map ${column.header}`} className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {options.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </Card>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onBack}>
          Back
        </Button>
        <Button type="button" size="sm" disabled={pending} onClick={onPreview}>
          {pending ? 'Previewing...' : 'Preview rows'}
        </Button>
      </div>
    </div>
  )
}

export function PreviewStep({
  summary,
  pending,
  error,
  onBack,
  onCommit,
}: {
  summary: ImportSummary
  pending: boolean
  error: string | null
  onBack: () => void
  onCommit: () => void
}) {
  return (
    <SummaryStep
      title="Preview import"
      summary={summary}
      error={error}
      actions={
        <>
          <Button type="button" variant="outline" size="sm" onClick={onBack}>
            Back
          </Button>
          <Button type="button" size="sm" disabled={pending} onClick={onCommit}>
            {pending ? 'Committing...' : 'Commit import'}
          </Button>
        </>
      }
    />
  )
}

export function ResultsStep({ summary, onDone }: { summary: ImportSummary; onDone: () => void }) {
  return (
    <SummaryStep
      title="Import complete"
      summary={summary}
      description={
        summary.error_count > 0 ? 'Rows with errors were skipped.' : 'All rows were imported.'
      }
      actions={
        <Button type="button" size="sm" onClick={onDone}>
          Done
        </Button>
      }
    />
  )
}

function SummaryStep({
  title,
  summary,
  description,
  error,
  actions,
}: {
  title: string
  summary: ImportSummary
  description?: string
  error?: string | null
  actions: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-4">
      <div>
        <h2 className="text-body-sm font-medium text-foreground">{title}</h2>
        {description && <p className="pt-1 text-body-sm text-muted-foreground">{description}</p>}
      </div>
      {error && <Alert>{error}</Alert>}
      <Card
        data-testid="contact-import-summary-panel"
        className="divide-y divide-border overflow-hidden"
      >
        <div className="grid grid-cols-1 divide-y divide-border sm:grid-cols-4 sm:divide-x sm:divide-y-0">
          <SummaryCount
            icon={FilePlus2}
            label="Created"
            value={summary.created_count}
            tone="success"
          />
          <SummaryCount
            icon={RefreshCw}
            label="Updated"
            value={summary.updated_count}
            tone="default"
          />
          <SummaryCount
            icon={AlertTriangle}
            label="Errors"
            value={summary.error_count}
            tone="warning"
          />
          {summary.notes_count !== undefined && (
            <SummaryCount
              icon={FilePlus2}
              label="Notes"
              value={summary.notes_count}
              tone="default"
            />
          )}
        </div>
        <div className="space-y-3 p-4">
          <h3 className="text-body-sm font-medium text-foreground">Row errors</h3>
          <RowErrorsTable errors={summary.errors} />
        </div>
      </Card>
      <div className="flex justify-end gap-2">{actions}</div>
    </div>
  )
}

function SummaryCount({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>
  label: string
  value: number
  tone: 'success' | 'warning' | 'default'
}) {
  return (
    <div
      aria-label={`${value} ${label.toLowerCase()}`}
      className="flex items-center gap-2 px-4 py-3"
    >
      <Icon
        className={cn(
          'h-4 w-4',
          tone === 'success' && 'text-success',
          tone === 'warning' && 'text-warning',
          tone === 'default' && 'text-muted-foreground',
        )}
        strokeWidth={1.5}
      />
      <span className="text-body-sm text-foreground tabular-nums">
        {value} {label.toLowerCase()}
      </span>
    </div>
  )
}
